import { Dropdown, DropDownProps, Menu, MenuProps } from "antd";
import React, {
  cloneElement,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TestID } from "../../shared/testids/testids";
import { COREMenu } from "../Navigation/COREMenu";
import { MenuInfo } from "rc-menu/lib/interface";
import "./COREDropdownMenu.less";
import "../Navigation/COREMenu.less";
import classNames from "classnames";
import {
  COREMenuItemGroup,
  COREMenuItemGroupProps,
} from "../Navigation/COREMenuItemGroup";
import { useAnchoredPopup } from "./useAnchoredPopup";

export type DropdownMenuItem =
  | {
      isGroupMenu?: never;
      groupHeaderProps?: never;
      component: React.ReactElement;
      closeWhenClick: boolean;
      // Holds the option above the scrolling list, under a divider. Any number
      // of options can be pinned, or none; pinned and unpinned options each
      // keep the order they were given in.
      isPinned?: boolean;
    }
  | {
      isGroupMenu: true;
      key: React.Key;
      groupHeaderProps: Omit<COREMenuItemGroupProps, "testID">;
      component: React.ReactElement[];
      closeWhenClick: boolean;
      isPinned?: never;
    };

type COREDropdownMenuProps = {
  interactionsMenu: DropdownMenuItem[];
  testID: TestID;
  isDisabled: boolean;
  selectedKeys?: MenuProps["selectedKeys"];
  placement?: DropDownProps["placement"];
  getPopupContainer?: DropDownProps["getPopupContainer"];
  onClickEvent?: (key: MenuInfo["key"]) => void;
  overlayStyle?: DropDownProps["overlayStyle"];
  // Caps the menu's height and scrolls it once it overflows. Set this rather
  // than putting maxHeight/overflow in overlayStyle: that styles the overlay
  // root, which antd overhangs by 4px with an invisible ::before mouse bridge,
  // so the root is permanently scrollable and paints a track even when only a
  // couple of items are shown.
  menuMaxHeight?: React.CSSProperties["maxHeight"];
  // Closes the popup on any leaf item click, including ones nested inside a
  // `CORESubMenu` — unlike each item's own `closeWhenClick`, which is only
  // ever wired onto the menu's own top-level items (see `handleOnClickOption`
  // below), so a click on a *nested* item never reaches it. Opt-in because a
  // consumer relying on `closeWhenClick: false` to keep a checklist-style
  // menu open after each pick should be unaffected.
  //
  // Uses rc-menu's own `Menu.onClick`, which fires for a `MenuItem` selection
  // anywhere in the tree, at any nesting depth, but never for a `SubMenu`
  // title/arrow click (that's a separate `onTitleClick`/`onOpenChange`
  // concern) — so it can't misfire while a user is still browsing a flyout.
  closeOnItemClick?: boolean;
  // See useAnchoredPopup. In a browser without anchor positioning,
  // `getPopupContainer` and `placement` apply as usual.
  anchorToTrigger?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export type COREDropdownMenuHandle = {
  // Closes the popup imperatively. `onTitleClick` on a `CORESubMenu` never
  // routes through this component's own click-to-close plumbing — rc-menu
  // fires it as a distinct event from a leaf item's `onClick`, never bubbling
  // through `Menu.onClick`/`onItemClick` the way a real selection does — so a
  // consumer that wants a title click to also close the popup has to do it
  // itself, via a ref to this.
  close: () => void;
};

export const COREDropdownMenu = forwardRef<
  COREDropdownMenuHandle,
  COREDropdownMenuProps
>(function COREDropdownMenu(
  {
    interactionsMenu,
    testID,
    isDisabled,
    selectedKeys,
    onClickEvent,
    placement,
    getPopupContainer,
    className,
    overlayStyle,
    menuMaxHeight,
    closeOnItemClick,
    anchorToTrigger,
    children,
  },
  ref
) {
  const [visiblePopup, setVisiblePopup] = useState(false);
  const anchored = useAnchoredPopup(Boolean(anchorToTrigger));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleVisibleChange = (visible: boolean) => {
    setVisiblePopup(visible);
    if (visible) anchored.onOpen(wrapperRef.current);
  };
  useImperativeHandle(ref, () => ({ close: () => setVisiblePopup(false) }), []);
  const handleOnClickOption = (
    info: MenuInfo,
    closeWhenClick: boolean = true
  ) => {
    const isModifiedNavigationClick =
      info.domEvent.metaKey ||
      info.domEvent.ctrlKey ||
      info.domEvent.shiftKey ||
      info.domEvent.altKey;

    if (isModifiedNavigationClick) {
      return;
    }

    handleVisibleChange(!closeWhenClick);
    onClickEvent && onClickEvent(info.key);
    info.domEvent.stopPropagation();
  };

  const renderInteraction = (interaction: DropdownMenuItem) => {
    if (interaction.isGroupMenu) {
      const items = interaction.component.map((c) => {
        return cloneElement(c, {
          testID: (testID + "-menu-item") as TestID,
          ...c.props,
          onClick: (info: MenuInfo) => {
            c.props.onClick && c.props.onClick();
            handleOnClickOption(info, interaction.closeWhenClick);
          },
        });
      });

      return (
        <COREMenuItemGroup
          key={interaction.key}
          {...interaction.groupHeaderProps}
          testID={(testID + "-portfolio-options-title") as TestID}
        >
          {items}
        </COREMenuItemGroup>
      );
    } else {
      return cloneElement(interaction.component, {
        testID: (testID + "-menu-item") as TestID,
        ...interaction.component.props,
        onClick: (info: MenuInfo) => {
          interaction.component.props.onClick &&
            interaction.component.props.onClick();
          handleOnClickOption(info, interaction.closeWhenClick);
        },
      });
    }
  };

  // Pinned options are lifted into a group of their own so the whole block —
  // options and the divider under them — sticks to the top as one box, however
  // many of them there are. See `COREDropdownMenu.less`.
  const pinnedMenu: DropdownMenuItem[] = [];
  const scrollingMenu: DropdownMenuItem[] = [];
  interactionsMenu?.forEach((interaction) =>
    (interaction.isPinned ? pinnedMenu : scrollingMenu).push(interaction)
  );

  const menu: React.ReactElement<typeof COREMenu> = (
    <COREMenu
      testID={(testID + "-menu") as TestID}
      selectedKeys={selectedKeys}
      containerStyle={
        menuMaxHeight === undefined
          ? undefined
          : { maxHeight: menuMaxHeight, overflowY: "auto" }
      }
      onClick={closeOnItemClick ? () => handleVisibleChange(false) : undefined}
    >
      {pinnedMenu.length === 0 ? null : (
        <Menu.ItemGroup
          key="pinned-options"
          data-testid={`${testID}/pinned-options`}
          className={classNames("core-dropdown-menu__pinned", {
            "core-dropdown-menu__pinned--sticky": menuMaxHeight !== undefined,
            "core-dropdown-menu__pinned--divided": scrollingMenu.length > 0,
          })}
        >
          {pinnedMenu.map(renderInteraction)}
        </Menu.ItemGroup>
      )}
      {scrollingMenu.map(renderInteraction)}
    </COREMenu>
  );

  return (
    <div
      className={"core-dropdown-menu"}
      style={anchored.anchorStyle}
      ref={wrapperRef}
    >
      <Dropdown
        overlayClassName={classNames(
          "core-menu-popup-container",
          "panel-dropdown-popup",
          anchored.popupClassName,
          className
        )}
        overlayStyle={{
          ...overlayStyle,
          ...anchored.anchorStyle,
          // Anchored, the menu is out of the popup's flow (see
          // COREAnchoredPopup.less), so it takes the width from here.
          ...(anchored.isAnchored &&
            overlayStyle?.width !== undefined && {
              "--core-anchored-width":
                typeof overlayStyle.width === "number"
                  ? `${overlayStyle.width}px`
                  : overlayStyle.width,
            }),
        }}
        overlay={menu}
        onOpenChange={handleVisibleChange}
        open={visiblePopup}
        trigger={["click"]}
        disabled={isDisabled}
        placement={placement}
        getPopupContainer={anchored.isAnchored ? undefined : getPopupContainer}
        transitionName={anchored.transitionName}
      >
        {children}
      </Dropdown>
    </div>
  );
});
